import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, Gift, Users, Check, Clock } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { brand } from "../../context/CompanyContext";

export default function ClientReferrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/client/referrals")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-[#a0b2af]">A carregar…</div>;
  if (!data) return null;

  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : BACKEND_URL}${data.share_url_hint}`;
  const shareText = `Experimente a ${brand()} — gestão profissional de limpezas para alojamentos locais. Use o meu código ${data.code} para se registar.`;

  function copy(txt, label) {
    navigator.clipboard?.writeText(txt).then(
      () => toast.success(`${label} copiado`),
      () => toast.error("Não foi possível copiar"),
    );
  }

  async function shareNative() {
    if (!navigator.share) { copy(shareUrl, "Link"); return; }
    try {
      await navigator.share({ title: `${brand()}`, text: shareText, url: shareUrl });
    } catch { /* cancelled */ }
  }

  function shareWhatsApp() {
    const text = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  }

  const { stats, available_discount, amount_per_referral } = data;

  return (
    <div className="space-y-8" data-testid="client-referrals-page">
      <header>
        <div className="eyebrow text-[#d4af37] flex items-center gap-2">
          <Gift size={14} /> Indicações
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Recomende. Ganhe.</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl leading-relaxed">
          Por cada cliente que se registe com o seu código e efetue o primeiro
          pagamento, recebe <strong className="text-[#d4af37]">{amount_per_referral}€ de desconto</strong>
          {" "}na sua próxima limpeza. Sem limites.
        </p>
      </header>

      {/* CODE CARD */}
      <section className="lux-card p-6 md:p-8 border-[#d4af37]/40" data-testid="referral-code-card">
        <div className="eyebrow text-[#a0b2af]">O seu código</div>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="font-mono text-3xl md:text-4xl text-[#d4af37] tracking-widest"
               data-testid="referral-code-value">
            {data.code}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => copy(data.code, "Código")}
                    className="px-3 py-2 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors inline-flex items-center gap-2"
                    data-testid="referral-copy-code">
              <Copy size={14} /> Código
            </button>
            <button onClick={() => copy(shareUrl, "Link")}
                    className="px-3 py-2 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors inline-flex items-center gap-2"
                    data-testid="referral-copy-link">
              <Copy size={14} /> Link completo
            </button>
            <button onClick={shareWhatsApp}
                    className="px-3 py-2 text-xs uppercase tracking-widest border border-[#10b981]/40 text-[#10b981] hover:bg-[#10b981]/10 transition-colors inline-flex items-center gap-2"
                    data-testid="referral-share-whatsapp">
              <Share2 size={14} /> WhatsApp
            </button>
            <button onClick={shareNative}
                    className="px-3 py-2 text-xs uppercase tracking-widest border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-colors inline-flex items-center gap-2"
                    data-testid="referral-share-native">
              <Share2 size={14} /> Partilhar
            </button>
          </div>
        </div>
        <div className="mt-4 text-xs text-[#a0b2af] font-mono break-all">
          {shareUrl}
        </div>
      </section>

      {/* STATS */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="referral-stats">
        <Stat label="Total convidados" value={stats.total} icon={Users} />
        <Stat label="A aguardar 1.º pagamento" value={stats.pending} icon={Clock} color="amber" />
        <Stat label="Créditos disponíveis" value={stats.unlocked} icon={Gift} color="gold"
              hint={`${available_discount.toFixed(0)}€`} />
        <Stat label="Já aplicados" value={stats.consumed} icon={Check} color="green" />
      </section>

      {/* AVAILABLE BANNER */}
      {stats.unlocked > 0 && (
        <div className="lux-card p-5 border-[#d4af37]/50 bg-[#d4af37]/5"
             data-testid="referral-available-banner">
          <div className="font-display text-lg text-[#d4af37]">
            Tem {available_discount.toFixed(0)}€ prontos a usar
          </div>
          <p className="text-sm text-[#a0b2af] mt-1">
            O desconto será aplicado automaticamente na(s) próxima(s) limpeza(s) que
            agendar — 5€ por cada. Cada indicação conta uma vez.
          </p>
        </div>
      )}

      {/* LIST */}
      <section>
        <div className="eyebrow text-[#a0b2af] mb-3">Histórico de indicações</div>
        {data.referrals.length === 0 ? (
          <div className="lux-card p-8 text-center text-sm text-[#a0b2af]"
               data-testid="referral-empty">
            Ainda não partilhou o seu código. Comece pelos amigos que gerem alojamentos locais.
          </div>
        ) : (
          <div className="space-y-2" data-testid="referral-list">
            {data.referrals.map((r) => (
              <div key={r.id} className="lux-card p-4 flex items-center justify-between gap-4"
                   data-testid={`referral-item-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-[#fdfbf7] truncate">{r.referred_name || "—"}</div>
                  <div className="text-xs text-[#a0b2af] truncate">{r.referred_email || ""}</div>
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color, hint }) {
  const colorMap = {
    gold: "text-[#d4af37]",
    green: "text-[#10b981]",
    amber: "text-[#f59e0b]",
  };
  return (
    <div className="lux-card p-4">
      <div className="eyebrow text-[10px] flex items-center gap-1.5 text-[#a0b2af]">
        <Icon size={12} /> {label}
      </div>
      <div className={`font-display text-3xl mt-2 ${colorMap[color] || "text-[#fdfbf7]"}`}>
        {value}
      </div>
      {hint && <div className={`text-xs mt-1 ${colorMap[color] || "text-[#a0b2af]"}`}>{hint}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "A aguardar pagamento", cls: "border-[#f59e0b]/40 text-[#f59e0b]" },
    unlocked: { label: "Desconto disponível", cls: "border-[#d4af37]/50 text-[#d4af37]" },
    consumed: { label: "Já aplicado", cls: "border-[#10b981]/40 text-[#10b981]" },
  };
  const s = map[status] || { label: status, cls: "border-[#1a4640] text-[#a0b2af]" };
  return (
    <span className={`px-3 py-1 text-[10px] uppercase tracking-widest border ${s.cls}`}>
      {s.label}
    </span>
  );
}
